<template>
  <div class="w-full flex flex-col" v-if="!!routeMeta">
    <div class="m-2 p-2 bg-white rounded shadow flex flex-col" style="max-height: 100%; height: 100%">
      <!-- Header bar -->
      <div class="flex flex-row items-center">
        <p class="p-2 text-lg font-medium text-primary flex-grow">
          {{ routeMeta.name }}
        </p>
        <IconMenu :actions="routeMeta.menuActions || []" :contextData="contextData" />
      </div>

      <!-- Breadcrumb -->
      <!-- <div class="flex flex-row items-center px-2 text-sm text-gray-500" v-if="breadcrumbs.length > 0">
        <span class="cursor-pointer hover:text-primary" @click="selectedFolderId = null">Root</span>
        <span v-for="crumb in breadcrumbs" :key="crumb.ID">
          <font-awesome-icon icon="chevron-right" class="mx-1 text-xs" />
          <span class="cursor-pointer hover:text-primary" @click="selectedFolderId = crumb.ID">{{ crumb.Name }}</span>
        </span>
      </div> -->

      <!-- Selected folder description -->
      <p class="px-2 py-1 text-sm text-gray-600" v-if="selectedFolder?.Description">
        {{ selectedFolder.Description }}
      </p>

      <Loading message="Loading" v-if="state.loading" />

      <!-- Split layout -->
      <div class="flex flex-row flex-grow overflow-hidden mt-2" v-if="!state.loading" style="min-height: 400px">
        <!-- Left panel: folder tree -->
        <div
          class="border-r border-gray-200 overflow-y-auto p-2 rounded-lg bg-gradient-to-br from-gray-100 to-gray-150 shadow-inner mx-2 my-2 py-2"
          style="min-width: 220px; max-width: 280px">
          <FolderTree :folders="allFolders" :selectedFolderId="selectedFolderId"
            :highlightedFolderId="highlightedFolderId" @folderSelected="onFolderSelected" @itemDropped="onItemDropped"
            @createFolder="openCreateFolderForm" />
        </div>

        <!-- Right panel: content -->
        <div class="flex-grow flex flex-col overflow-auto pl-2">
          <!-- Child folders -->
          <div v-if="childFolders.length > 0" class="mb-2">
            <p class="px-2 py-1 text-xs font-medium text-gray-400 uppercase">Folders</p>
            <div v-for="folder in childFolders" :key="folder.ID"
              class="flex flex-row items-center px-2 py-2 cursor-pointer rounded hover:bg-gray-100"
              :class="{ 'bg-blue-100 border border-blue-300': highlightedFolderId === folder.ID }"
              :data-folder-id="folder.ID" @click="selectedFolderId = folder.ID">
              <font-awesome-icon icon="folder" class="mr-2 text-primary" />
              <p class="text-sm">{{ folder.Name }}</p>
            </div>
          </div>

          <!-- Items table -->
          <div class="flex-grow flex flex-col">
            <p class="px-2 py-1 text-xs font-medium text-gray-400 uppercase" v-if="childFolders.length > 0">Items</p>
            <!-- <Input ref="quickSearch" label="Quick Search" v-model="quickFilter" /> -->
            <DataTable ref="table" :items="folderItems" @itemSelected="itemSelected" @gridReady="onGridReady"
              :paginationAutoPageSize="true" :quickFilter="quickFilter" :tableSettings="tableSettings" :rowDrag="true"
              :rowDragFn="onRowDragFn" />
          </div>
        </div>
      </div>
    </div>
  </div>
  <DynamicForm ref="form" @submitted="$forceUpdate" />
  <ContextMenu ref="contextMenu" />
</template>

<script setup>
import { computed, onMounted, watch, ref, reactive, onBeforeMount, onBeforeUnmount, nextTick, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { db } from "../../db.js";
import DynamicForm from "../DynamicForm/DynamicForm.vue";
import FolderTree from "./FolderTree.vue";
import { DataTable, IconMenu, Loading, Input, ContextMenu } from "@comp/index.js";
import { useToast } from "vue-toastification";

const instance = getCurrentInstance();
const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

let table = ref(null);
let form = ref(null);
let contextMenu = ref(null);
let routeMeta = ref(null);
const folderPanel = ref(null);

const quickFilter = ref("");
const selectedFolderId = ref(null);
const highlightedFolderId = ref(null);
const _gridApi = ref(null);
const _registeredDropZones = ref([]);
let _observer = null;

let state = reactive({
  loading: true,
});

const itemBeingDragged = ref(null);

const openCreateFolderForm = () => {
  

  routeMeta.value.menuActions.find(a => a.label === 'Create Folder')?.action(contextData.value);
}

// --- Folder data ---

const allFolders = computed(() => {
  const raw = store.state[routeMeta?.value?.folderDataPath];
  if (!raw) return [];
  return Object.values(raw);
});

const selectedFolder = computed(() => {

  if (!selectedFolderId.value) {
    
    return {
      "description": "Root folder - contains all items not in a folder",
    };
  }
  return allFolders.value.find((f) => f.ID === selectedFolderId.value) || null;
});

const childFolders = computed(() => {
  if (!selectedFolderId.value) {
    return allFolders.value.filter((f) => !f.Parent);
  }
  return allFolders.value.filter((f) => f.Parent === selectedFolderId.value);
});

const breadcrumbs = computed(() => {
  if (!selectedFolderId.value) return [];
  const crumbs = [];
  let current = selectedFolder.value;
  while (current) {
    crumbs.unshift(current);
    current = current.Parent ? allFolders.value.find((f) => f.ID === current.Parent) : null;
  }
  return crumbs;
});

// --- Item data ---

const _items = computed(() => store.state[routeMeta?.value?.dataPath]);

const folderItems = computed(() => {
  if (!_items.value) return [];
  const refField = routeMeta?.value?.folderRefField || "FolderID";
  return Object.values(_items.value).filter((item) => {
    if (!selectedFolderId.value) {
      return !item[refField];
    }
    return item[refField] === selectedFolderId.value;
  });
});

// --- Table settings ---

const tableSettings = computed(() => {
  if (!store.state.user?.Settings?.tables?.[routeMeta?.value?.tableSettings]) {
    return null;
  }
  store.state.user.Settings.tables[routeMeta?.value?.tableSettings].columnState[0].dndSource = true;
  return {
    ...store.state.user.Settings.tables[routeMeta?.value?.tableSettings],
    ...(routeMeta?.value?.tableSettingsOverride || {}),
  };
});

// --- Context data ---

const contextData = computed(() => {
  return {
    currentRoute: route,
    form: form.value,
    items: folderItems.value,
    store,
    router,
    db,
    toast,
    instance,
    meta: routeMeta.value?.formMetadata,
    selectedFolderId: selectedFolderId.value,
    selectedFolder: selectedFolder.value,
    allFolders: allFolders.value,
  };
});

// --- Navigation ---

const onFolderSelected = (id) => {
  selectedFolderId.value = id;
};

const onRowDragFn = (params) => {
  itemBeingDragged.value = params.rowNode.data;
};

const onItemDropped = async (folderId) => {
  
  const refField = routeMeta?.value?.folderRefField || "FolderID";

  try {
    await db.update(`${routeMeta.value.dataPath}/${itemBeingDragged.value.ID}`, { [refField]: folderId });
    toast.success(`Moved to ${folderId ? allFolders.value.find(f => f.ID === folderId)?.Name : "Root"}`);
  } catch (err) {
    toast.error(err, "Failed to move item");
  }
};

const itemSelected = (e) => {
  contextMenu.value.toggle(
    e,
    { rowActions: routeMeta?.value?.rowActions || [] },
    {
      ...contextData.value,
      rowItem: e.data,
      item: e.data,
    },
    e.data.Title || e.data.Number || e.data.Name || "Item"
  );
};

// --- Data loading (follows ItemListView pattern) ---

function getListData(_meta) {
  if (_meta?.value?.folderDataPath) {
    db.watch(_meta.value.folderDataPath, store);
  }

  if (typeof _meta?.value?.dataQuery === "function") {
    _meta?.value?.dataQuery(store);
  } else if (_meta?.value?.dataQuery?.key && _meta?.value?.dataQuery?.value) {
    db.query(_meta.value.dataPath, _meta.value.dataQuery.key, _meta.value.dataQuery.value, store);
  } else {
    if (!_meta?.value?.dataPath) return;
    db.watch(_meta.value.dataPath, store);
  }
  state.loading = false
}

onMounted(() => {
  getListData(routeMeta);

  // Re-register drop zones when folder tree DOM changes (expand/collapse)
  nextTick(() => {
    if (folderPanel.value) {
      setTimeout(() => {
        
        state.loading = false;
      }, 500);
    }
  });
});

onBeforeUnmount(() => {
  // cleanupDropZones();
  if (_observer) {
    _observer.disconnect();
    _observer = null;
  }
});

onBeforeMount(async () => {
  if (route.meta.onBeforeMount) {
    await route.meta.onBeforeMount();
  }

  let meta = !!route.meta.lazyLoadMeta ? await route.meta.lazyLoadMeta() : {};

  routeMeta.value = {
    ...route.meta,
    ...meta,
  };
});

watch(_items, (val) => {
  
  

}, { immediate: true });

watch(() => allFolders.value, () => {
  // nextTick(() => registerDropZones());
}, { deep: true });

watch(() => routeMeta.value, () => {
  state.loading = true;
  getListData(routeMeta);
}, { deep: true });
</script>
